import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Mail, Save, Send, Server, Lock, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const AdminSMTPConfigPage = () => {
  const { language } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [config, setConfig] = useState({
    host: '',
    port: 587,
    user: '',
    password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    password_set: false
  });

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await api.get('/admin/smtp-config');
      setConfig(prev => ({ ...prev, ...response.data, password: '' }));
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...config };
      if (!payload.password) delete payload.password;
      delete payload.password_set;
      delete payload.config_id;
      delete payload.updated_at;
      delete payload.updated_by;
      await api.put('/admin/smtp-config', payload);
      toast.success(language === 'fr' ? 'Configuration SMTP sauvegardee' : 'SMTP config saved');
      loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving config');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error(language === 'fr' ? 'Entrez une adresse email de test' : 'Enter a test email');
      return;
    }
    setTesting(true);
    try {
      await api.post('/admin/smtp-test', { email: testEmail });
      toast.success(language === 'fr' ? `Email de test envoye a ${testEmail}` : `Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'SMTP test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="smtp-config-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Mail className="w-6 h-6 text-primary" />
              {language === 'fr' ? 'Configuration SMTP' : 'SMTP Configuration'}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {language === 'fr' 
                ? 'Parametres du serveur email pour les notifications, verifications et reinitialisation de mot de passe'
                : 'Email server settings for notifications, verifications and password resets'}
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} data-testid="smtp-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? '...' : (language === 'fr' ? 'Sauvegarder' : 'Save')}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Server className="w-5 h-5" />
              {language === 'fr' ? 'Serveur SMTP' : 'SMTP Server'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' ? 'Parametres de connexion au serveur de messagerie' : 'Mail server connection settings'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2 space-y-2">
                <Label>{language === 'fr' ? 'Serveur SMTP (host)' : 'SMTP Host'}</Label>
                <Input 
                  value={config.host} 
                  onChange={(e) => setConfig({ ...config, host: e.target.value })}
                  placeholder="smtp.example.com"
                  data-testid="smtp-host-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Port' : 'Port'}</Label>
                <Input 
                  type="number"
                  value={config.port} 
                  onChange={(e) => setConfig({ ...config, port: parseInt(e.target.value) || 587 })}
                  placeholder="587"
                  data-testid="smtp-port-input"
                />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Utilisateur' : 'Username'}</Label>
                <Input 
                  value={config.user} 
                  onChange={(e) => setConfig({ ...config, user: e.target.value })}
                  placeholder="no-reply@domain.com"
                  data-testid="smtp-user-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5" />
                  {language === 'fr' ? 'Mot de passe' : 'Password'}
                  {config.password_set && (
                    <Badge variant="outline" className="text-xs ml-1">
                      <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                      {language === 'fr' ? 'Defini' : 'Set'}
                    </Badge>
                  )}
                </Label>
                <Input 
                  type="password"
                  value={config.password} 
                  onChange={(e) => setConfig({ ...config, password: e.target.value })}
                  placeholder={config.password_set ? '********' : (language === 'fr' ? 'Entrer mot de passe' : 'Enter password')}
                  data-testid="smtp-password-input"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
              <Switch 
                checked={config.use_tls} 
                onCheckedChange={(checked) => setConfig({ ...config, use_tls: checked })}
                data-testid="smtp-tls-switch"
              />
              <div>
                <p className="font-medium text-sm">{language === 'fr' ? 'Utiliser TLS/STARTTLS' : 'Use TLS/STARTTLS'}</p>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Recommande pour la securite des communications' : 'Recommended for secure communications'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Mail className="w-5 h-5" />
              {language === 'fr' ? 'Expediteur' : 'Sender'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Nom de l\'expediteur' : 'From Name'}</Label>
                <Input 
                  value={config.from_name} 
                  onChange={(e) => setConfig({ ...config, from_name: e.target.value })}
                  placeholder="SNIGJ - Justice RDC"
                  data-testid="smtp-from-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Email expediteur' : 'From Email'}</Label>
                <Input 
                  type="email"
                  value={config.from_email} 
                  onChange={(e) => setConfig({ ...config, from_email: e.target.value })}
                  placeholder="no-reply@domain.com"
                  data-testid="smtp-from-email-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Send className="w-5 h-5" />
              {language === 'fr' ? 'Test de connexion' : 'Connection Test'}
            </CardTitle>
            <CardDescription>
              {language === 'fr' 
                ? 'Envoyez un email de test pour verifier la configuration' 
                : 'Send a test email to verify the configuration'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input 
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder={language === 'fr' ? 'Adresse email de test...' : 'Test email address...'}
                className="flex-1"
                data-testid="smtp-test-email-input"
              />
              <Button onClick={handleTest} disabled={testing} variant="outline" data-testid="smtp-test-btn">
                {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                {testing ? '...' : (language === 'fr' ? 'Envoyer test' : 'Send test')}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-200 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Mail className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm space-y-1">
                <p className="font-medium text-amber-800 dark:text-amber-200">
                  {language === 'fr' ? 'Delivrabilite des emails' : 'Email Deliverability'}
                </p>
                <p className="text-amber-700 dark:text-amber-300">
                  {language === 'fr' 
                    ? 'Pour eviter que vos emails arrivent en spam, configurez les enregistrements DNS suivants pour votre domaine : SPF, DKIM et DMARC. Contactez votre hebergeur pour plus d\'informations.'
                    : 'To prevent emails from landing in spam, configure the following DNS records for your domain: SPF, DKIM and DMARC. Contact your hosting provider for more info.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminSMTPConfigPage;
